import { ReactNode } from 'react';

import { Box, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import { FormCard } from '@/components';
import { Table } from '@/components/TailwindComponents';
import { useKaseContext } from '@/hooks';
import { getActiveLicensePlate } from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import { IProperty } from '@/models';
import { useKaseMainContext } from '@/pages/Kases/KaseEdit/KaseMainContext';
import {
  formatPersonName,
  getFormattedDateTime,
  getMasterNameDisplayName,
} from '@/utils';

import {
  IPropertyDetailField,
  PropertyDetailsGeneralCells,
  PropertyTypeSpecificFields,
} from './data';

interface PropertyDetailsCardProps {
  property: IProperty;
  caseId?: string;
  cfsId?: string;
  propertyTypeLabel?: string;
  length?: boolean;
}

const getFieldValue = (
  property: IProperty,
  field: IPropertyDetailField,
  caseId?: string,
  cfsId?: string,
  propertyTypeLabel?: string,
  PropertyInvolvementList?: any,
  drugTypes?: any,
): string | null => {
  switch (field.key) {
    case 'number':
      return property.number || null;
    case 'name':
      return property.name || null;
    case 'typeTag':
      return property.typeTag || null;
    case 'involvement':
      return PropertyInvolvementList.get(property.involvement)?.label || null;
    case 'description':
      return property.description || null;
    case 'owner':
      return property.owner ? getMasterNameDisplayName(property.owner) : null;
    case 'value':
      return property.value != null ? String(property.value) : null;
    case 'loggedInfo': {
      const info = property.loggedInfo;
      if (!info) return null;
      const name = formatPersonName(info.loggedBy);
      const date = info.loggedDate
        ? getFormattedDateTime(new Date(info.loggedDate).toISOString())
        : '';
      return [name, date].filter(Boolean).join(' - ') || null;
    }
    case 'inboundTransferredInfo': {
      const info = property.inboundTransferredInfo;
      if (!info) return null;
      const name = formatPersonName(info.transferredBy);
      const date = info.transferredDate
        ? getFormattedDateTime(new Date(info.transferredDate).toISOString())
        : '';
      return [name, date].filter(Boolean).join(' - ') || null;
    }
    case 'type':
      return propertyTypeLabel || property.type || null;
    case 'caseId':
      return caseId || null;
    case 'cfsId':
      return cfsId || null;
    case 'cash.amount':
      return property.cash?.amount != null
        ? String(property.cash.amount)
        : null;
    case 'cash.currency':
      return property.cash?.currency || null;
    case 'jewelry.type':
      return property.jewelry?.type?.type || null;
    case 'alcohol.brand':
      return property.alcohol?.brand?.name || null;
    case 'drugs.type':
      return (
        property.drugs
          ?.map((drug) =>
            drug.type ? drugTypes.get(drug.type?.toString())?.label : false,
          )
          .filter(Boolean)
          .join(', ') || null
      );
    case 'drugs.quantity':
      return (
        property.drugs
          ?.map((drug) =>
            drug.quantity != null
              ? `${drug.quantity}${
                  drug.measurementType ? ` ${drug.measurementType}` : ''
                }`
              : null,
          )
          .filter(Boolean)
          .join(', ') || null
      );
    case 'firearm.type':
      return property.firearm?.type || null;
    case 'firearm.make':
      return property.firearm?.make?.name || null;
    case 'firearm.model':
      return property.firearm?.model?.name || null;
    case 'knife.make':
      return property.knife?.make?.name || null;
    case 'knife.model':
      return property.knife?.model?.name || null;
    case 'explosive.type':
      return property.explosive?.type || null;
    case 'vehicle.plateNumber':
      return (
        getActiveLicensePlate(property.vehicle?.masterVehicle)?.number || null
      );
    case 'vehicle.make':
      return property.vehicle?.masterVehicle?.make || null;
    case 'vehicle.model':
      return property.vehicle?.masterVehicle?.model || null;
    case 'vehicle.color':
      return property.vehicle?.masterVehicle?.color?.name || null;
    default:
      return null;
  }
};

export const PropertyDetailsCard = ({
  property,
  caseId,
  propertyTypeLabel,
}: PropertyDetailsCardProps) => {
  const typeSpecificKey = Object.keys(PropertyTypeSpecificFields).find(
    (key) => {
      const currentValue = property[key as keyof IProperty];

      return Array.isArray(currentValue)
        ? currentValue?.length > 0
        : currentValue != null;
    },
  );
  const { kase } = useKaseContext();

  const { DrugType: drugTypes, PropertyInvolvementList } = useKaseMainContext();

  const typeSpecificFields = typeSpecificKey
    ? PropertyTypeSpecificFields[typeSpecificKey]
    : [];
  return (
    <FormCard
      sx={{
        '& .divider': {
          marginTop: '5px',
        },
      }}
      title={
        <Box
          className="cardTitle"
          style={{ padding: '5px 10px 0' }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="inherit" component="span">
            Property details
          </Typography>
          <Typography variant="inherit" component="span">
            Item No. {property.itemNumber}
          </Typography>
        </Box>
      }
    >
      <Table style={{ margin: '0 10px' }}>
        <TableBody>
          {[...PropertyDetailsGeneralCells, [...typeSpecificFields]].map(
            (row) => {
              const rowsParsed = row.reduce<{
                headers: ReactNode[];
                content: ReactNode[];
              }>(
                (acc, field) => {
                  return {
                    headers: [
                      ...acc.headers,
                      <th
                        key={`${field.key}-head`}
                        {...(field.colspan && { colspan: field.colspan })}
                        style={{
                          textAlign: 'left',
                          padding: '15px 5px 5px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {field.label}
                      </th>,
                    ],
                    content: [
                      ...acc.content,
                      <TableCell
                        key={`${field.key}-cell`}
                        {...(field.colspan && { colspan: field.colspan })}
                        sx={{ verticalAlign: 'top' }}
                      >
                        {' '}
                        {getFieldValue(
                          property,
                          field,
                          caseId,
                          kase?.cfsNumber,
                          propertyTypeLabel,
                          PropertyInvolvementList,
                          drugTypes,
                        )}
                      </TableCell>,
                    ],
                  };
                },
                { headers: [], content: [] },
              );

              return (
                <>
                  <TableRow>{[...rowsParsed.headers]}</TableRow>
                  <TableRow>{[...rowsParsed.content]}</TableRow>
                </>
              );
            },
          )}
        </TableBody>
      </Table>
    </FormCard>
  );
};
